import React from 'react'
import { Box } from '@mui/material'
import { useLocation } from 'react-router-dom'
import PageTitle from '../../../components/PageTitle'
import { PageHeader, SectionIntro, SegmentTabs } from '../components/SetupUi'
import KpiStrip from './KpiStrip'

/**
 * Shell of every PTL Merchant Setup screen that is not an onboarding step: title, one-line purpose and actions, then the
 * KPI cards, then one body. Onboarding steps use PtlWizardShell instead.
 *
 * A page with sections (`tabs`) shows the title, then the section tabs, then the open section's purpose, actions, KPI
 * cards and body, so everything that changes with the tab sits under it.
 *
 * @param kpis        items for KpiStrip; every screen has them (rule: KPI cards on every screen)
 * @param kpiMinWidth narrowest KPI card in px (KpiStrip minWidth), for screens with more cards
 * @param breadcrumb  shown above the title on screens opened from another one (Upload, History)
 * @param tabs        sections with their own routes, [{ key, label, to, count? }]; the open one is the current route
 * @param tabTitle    the open section's name, for the browser tab and the KPI cards' label
 * @param eyebrow     the module above the title (default PTL Merchant Setup)
 */
export default function PtlPage({ title, tabTitle, eyebrow, description, actions, kpis, kpisLoading, kpisLabel, kpiMinWidth, breadcrumb, tabs, children }) {
  const { pathname } = useLocation()
  const name = tabTitle || title
  return (
    <Box sx={{ maxWidth: 1640, mx: 'auto' }}>
      <PageTitle>{name}</PageTitle>
      {breadcrumb}
      {tabs ? (
        <>
          <PageHeader title={title} eyebrow={eyebrow} />
          <SegmentTabs value={tabs.find((t) => t.to === pathname)?.key || false} items={tabs} ariaLabel={`${title} sections`} />
          <SectionIntro description={description} actions={actions} />
        </>
      ) : (
        <PageHeader title={title} eyebrow={eyebrow} description={description} actions={actions} />
      )}
      {kpis && <KpiStrip items={kpis} loading={kpisLoading} ariaLabel={kpisLabel || `${name} key figures`} minWidth={kpiMinWidth} />}
      {children}
    </Box>
  )
}
